import { useEffect, useRef, useState, useCallback } from 'react'
import { useAuthStore } from '@/store/authStore'
import { Shield, Clock } from 'lucide-react'

const TIMEOUT_MS = 30 * 60 * 1000   // 30 minutes — HIPAA standard
const WARNING_MS = 25 * 60 * 1000   // Show warning at 25 minutes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']
const THROTTLE_MS = 30000            // Only update activity timestamp every 30s

export default function SessionTimeout() {
  const { token, logout } = useAuthStore()
  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(300)
  const lastActivity = useRef(Date.now())
  const warningTimer = useRef(null)
  const logoutTimer = useRef(null)
  const countdownInterval = useRef(null)
  const lastThrottle = useRef(0)

  const resetTimers = useCallback(() => {
    lastActivity.current = Date.now()
    setShowWarning(false)

    clearTimeout(warningTimer.current)
    clearTimeout(logoutTimer.current)
    clearInterval(countdownInterval.current)

    if (!token) return

    warningTimer.current = setTimeout(() => {
      setShowWarning(true)
      setCountdown(Math.ceil((TIMEOUT_MS - WARNING_MS) / 1000))

      countdownInterval.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, WARNING_MS)

    logoutTimer.current = setTimeout(() => {
      setShowWarning(false)
      logout()
    }, TIMEOUT_MS)
  }, [token, logout])

  const handleActivity = useCallback(() => {
    const now = Date.now()
    if (now - lastThrottle.current < THROTTLE_MS) return
    lastThrottle.current = now
    resetTimers()
  }, [resetTimers])

  useEffect(() => {
    if (!token) return

    resetTimers()

    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, handleActivity, { passive: true })
    )

    return () => {
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, handleActivity)
      )
      clearTimeout(warningTimer.current)
      clearTimeout(logoutTimer.current)
      clearInterval(countdownInterval.current)
    }
  }, [token, handleActivity, resetTimers])

  if (!showWarning || !token) return null

  const minutes = Math.floor(countdown / 60)
  const seconds = countdown % 60

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-dark-800 border border-dark-600 rounded-xl w-full max-w-sm p-6 animate-slide-up text-center">
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-amber-500/10">
          <Shield size={28} className="text-amber-400"/>
        </div>
        <h2 className="text-lg font-bold text-white mb-2">Session Expiring</h2>
        <p className="text-dark-400 text-sm mb-4">
          For HIPAA compliance, your session will automatically end due to inactivity.
        </p>
        <div className="flex items-center justify-center gap-2 mb-5">
          <Clock size={16} className="text-amber-400"/>
          <span className="text-2xl font-bold font-mono text-amber-400">
            {minutes}:{seconds.toString().padStart(2, '0')}
          </span>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { logout() }}
            className="flex-1 btn-secondary text-sm">
            Log Out Now
          </button>
          <button onClick={resetTimers}
            className="flex-1 btn-primary text-sm">
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  )
}
